import React, { useState, useEffect } from "react";
import "./ConversationList.css"; // Importing the CSS file for styling
import axios from "axios";
import dayjs from "dayjs";
import {
  Button,
  Spin,
  Alert,
  Modal,
  Space,
  Tag,
  Typography,
  Select,
  Row,
  Col,
} from "antd";
import { EyeOutlined, SyncOutlined } from "@ant-design/icons";
import API_BASE_URL from "../../config/api";
import ConversationDetailTabs from "./ConversationDetailTabs";
import { ELEVENLABS_CONFIG } from "../../config/elevenlabs-api";
import CustomPagination from "../paginations/CustomPagination";

const { Title, Text } = Typography;

const ConversationList = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [conversationDetail, setConversationDetail] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    // Clear conversations when agent changes
    setConversations([]);
    // Fetch conversations
    fetchConversations(selectedAgent, true);
    const interval = setInterval(() => {
      console.log("🔄 Auto-refreshing conversations...");
      fetchConversations(
        selectedAgent,
        pagination.current,
        pagination.pageSize,
        true
      );
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedAgent]);

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/agents`, {
        headers: { Authorization: token },
      });
      if (response.data.success) {
        // Add 'All Agents' option at the top
        const allAgentsOption = { name: 'All Agents', elevenlabs_agent_id: 'ALL' };
        const agentsList = [allAgentsOption, ...response.data.data];
        setAgents(agentsList);
        // Select 'All Agents' by default
        setSelectedAgent('ALL');
      }
    } catch (err) {
      setError("Failed to fetch agents");
    }
  };

  const fetchConversations = async (agentId, reset = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = {};
      if (agentId && agentId !== 'ALL') {
        params.agentId = agentId;
      }
      const response = await axios.get(`${API_BASE_URL}/conversations`, {
        headers: {
          Authorization: token,
        },
        params,
      });
      setConversations(response.data.conversations || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.conversations?.length || 0,
      }));
    } catch (err) {
      setError("Failed to fetch conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationDetail = async (conversationId) => {
    setDetailLoading(true);
    setDetailError("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${ELEVENLABS_CONFIG.BASE_URL}/conversations/${conversationId}`,
        {
          headers: {
            "xi-api-key": ELEVENLABS_CONFIG.API_KEY,
          },
        }
      );
      setConversationDetail(response.data);
    } catch (err) {
      setDetailError("Failed to fetch conversation details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetail = (conversation) => {
    setSelectedConversation(conversation);
    fetchConversationDetail(conversation.conversation_id || conversation.id);
    setShowDetail(true);
  };

  const handleDetailClose = () => {
    setSelectedConversation(null);
    setConversationDetail(null);
    setShowDetail(false);
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      current: 1,
    }));
  };

  const syncConversations = async () => {
    setSyncing(true);
    setError("");
    const token = localStorage.getItem("token");
    if (!selectedAgent) {
      setError("Please select an agent to sync.");
      setSyncing(false);
      return;
    }
    try {
      if (selectedAgent === "ALL") {
        // Filter out the 'ALL' option and sync each agent
        const realAgents = agents.filter(a => a.elevenlabs_agent_id && a.elevenlabs_agent_id !== "ALL");
        let syncErrors = [];
        for (const agent of realAgents) {
          try {
            console.log("Syncing agent:", agent.elevenlabs_agent_id);
            await axios.post(
              `${API_BASE_URL}/conversations/sync-elevenlabs`,
              { agentId: agent.elevenlabs_agent_id },
              {
                headers: { Authorization: token },
              }
            );
          } catch (err) {
            syncErrors.push(agent.name || agent.elevenlabs_agent_id);
            console.error(`Sync error for agent ${agent.elevenlabs_agent_id}:`, err);
          }
        }
        if (syncErrors.length > 0) {
          setError(`Failed to sync: ${syncErrors.join(", ")}`);
        }
        // After all syncs, fetch all conversations
        fetchConversations("ALL");
      } else {
        console.log("Starting conversation sync with agent:", selectedAgent);
        const response = await axios.post(
          `${API_BASE_URL}/conversations/sync-elevenlabs`,
          { agentId: selectedAgent },
          {
            headers: {
              Authorization: token,
            },
          }
        );
        console.log("Sync response:", response.data);
        fetchConversations(selectedAgent);
      }
    } catch (err) {
      console.error("Sync error:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to sync conversations";
      setError(`Failed to sync conversations: ${errorMessage}`);
    } finally {
      setSyncing(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "N/A";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "done":
        return "green";
      case "failed":
        return "red";
      case "initiated":
        return "yellow";
      case "in_progress":
        return "blue";
      default:
        return "default";
    }
  };

  const getDispositionColor = (status) => {
    switch (status?.toLowerCase()) {
      case "interested":
        return "green";
      case "not interested":
        return "red";
      default:
        return "default"; // grey if unknown
    }
  };

  const columns = [
    {
      title: "Agent Name",
      dataIndex: ["Agent", "name"],
      key: "agentName",
      render: (name, record) => name || record.summary_title || "Unknown Agent",
    },
    {
      title: "Start Time",
      dataIndex: "start_time",
      key: "startTime",
      render: (startTime) =>
        startTime ? dayjs(startTime).format("DD-MM-YYYY hh:mm:ss A") : "N/A",
    },
    {
      title: "End Time",
      dataIndex: "end_time",
      key: "endTime",
      render: (end_time) =>
        end_time ? dayjs(end_time).format("DD-MM-YYYY hh:mm:ss A") : "N/A",
    },
    {
      title: "Duration",
      dataIndex: "call_duration",
      key: "duration",
      render: (duration) => formatDuration(duration),
    },
    {
      title: "Summary",
      dataIndex: "summary",
      key: "summary",
      render: (summary) => (
        <Text ellipsis={{ tooltip: summary }}>{summary || "No summary"}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={getStatusColor(status)}
          style={{
            minWidth: "100px",
            textAlign: "center",
            display: "inline-block",
          }}
        >
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Disposition Status",
      dataIndex: "Dispositions",
      key: "dispositions",
      render: (dispositions) =>
        dispositions?.length > 0 ? (
          <Space>
            {dispositions.map((d, index) => (
              <Tag color={getDispositionColor(d.status)} key={index}>
                {d.status?.toUpperCase()}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">NO DISPOSITION</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
          size="small"
        >
          View Details
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" tip="Loading conversations..." />
      </div>
    );
  }

  return (
    <div>
      <Row
        className="m-4"
        justify="space-between"
        align="middle"
        gutter={[16, 16]}
      >
        <Col xs={24} sm={24} md={12} lg={12}>
          <Title level={2} style={{ margin: 0 }}>
            Report History
          </Title>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Row gutter={[16, 16]} justify="end">
            <Col xs={24} sm={12} md={12} lg={12}>
              <Select
                className="report-select"
                placeholder="Filter by Agent"
                allowClear
                value={selectedAgent || undefined}
                onChange={(value) => setSelectedAgent(value || null)}
                options={agents.map((agent) => ({
                  label: agent.name,
                  value: agent.elevenlabs_agent_id || agent.id,
                }))}
                style={{ width: "100%", minWidth: 150 }}
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Button
                type="primary"
                className="report-button"
                icon={<SyncOutlined />}
                onClick={syncConversations}
                loading={syncing}
                style={{ width: "100%" }}
              >
                Sync Data
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError("")}
        />
      )}

      <div className="rounded-2 table-responsive m-4">
        <table
          className="table align-middle mb-0 shadow-sm"
          style={{ tableLayout: "auto" }}
        >
          <thead className="table-light">
            <tr>
              <th>Agent Name</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Duration</th>
              <th>Summary</th>
              <th>Status</th>
              <th>Disposition Status</th>
              <th style={{ width: "150px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {conversations
              .slice(
                (pagination.current - 1) * pagination.pageSize,
                pagination.current * pagination.pageSize
              )
              .map((conversation, index) => (
                <tr key={conversation.id || conversation.conversation_id}>
                  <td>
                    {conversation.Agent?.name ||
                      conversation.summary_title ||
                      "Unknown Agent"}
                  </td>
                  <td>
                    {conversation.start_time
                      ? dayjs(conversation.start_time).format(
                          "DD-MM-YYYY hh:mm:ss A"
                        )
                      : "N/A"}
                  </td>
                  <td>
                    {conversation.end_time
                      ? dayjs(conversation.end_time).format(
                          "DD-MM-YYYY hh:mm:ss A"
                        )
                      : "N/A"}
                  </td>
                  <td>{formatDuration(conversation.call_duration)}</td>
                  <td>
                    <span title={conversation.summary}>
                      {conversation.summary || "No summary"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge bg-${
                        getStatusColor(conversation.status) === "green"
                          ? "success"
                          : getStatusColor(conversation.status) === "red"
                          ? "danger"
                          : getStatusColor(conversation.status) === "yellow"
                          ? "warning"
                          : getStatusColor(conversation.status) === "blue"
                          ? "info"
                          : "secondary"
                      }`}
                    >
                      {conversation.status?.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    {conversation.Dispositions?.length > 0
                      ? conversation.Dispositions.map((d, index) => (
                          <span
                            key={index}
                            className={`badge bg-${
                              getDispositionColor(d.status) === "green"
                                ? "success"
                                : "secondary"
                            }`}
                          >
                            {d.status?.toUpperCase()}
                          </span>
                        ))
                      : "NO DISPOSITION"}
                  </td>
                  <td style={{ paddingRight: "8px", whiteSpace: "nowrap" }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleViewDetail(conversation)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <CustomPagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <Modal
        title="Report Details"
        open={showDetail}
        onCancel={handleDetailClose}
        footer={null}
        width={900}
        destroyOnClose
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {detailLoading ? (
          <div
            className="m-4"
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <Spin size="large" />
          </div>
        ) : detailError ? (
          <Alert
            message="Error"
            description={detailError}
            type="error"
            showIcon
          />
        ) : (
          <ConversationDetailTabs
            conversation={{ ...selectedConversation, ...conversationDetail }}
            onClose={handleDetailClose}
          />
        )}
      </Modal>
    </div>
  );
};

export default ConversationList;
